import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Zap, Gauge, Wrench, Shield, Smartphone, Award, ArrowRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReviewCard from '../components/ReviewCard';
import ReviewForm from '../components/ReviewForm';
import HeroCarousel from '../components/HeroCarousel';
import FindMyVehicle from '../components/FindMyVehicle';
import { reviewsAPI, categoriesAPI } from '../services/api';
import { useSEO } from '../hooks/useSEO';

const features = [
  { icon: 'Zap', title: 'Instant Power Gains', description: 'Increase BHP and torque immediately after installation' },
  { icon: 'Gauge', title: 'Better Fuel Economy', description: 'Improved MPG through optimized engine mapping' },
  { icon: 'Wrench', title: 'DIY Installation', description: 'Simple 5-30 minute plug-and-play setup, detailed instructions included' },
  { icon: 'Shield', title: 'Warranty Friendly', description: 'Completely traceless once removed, insurance friendly' },
  { icon: 'Smartphone', title: 'Bluetooth Control', description: 'Adjust settings via mobile app on compatible models' },
  { icon: 'Award', title: 'UK Engineered', description: 'Designed and tested in the UK with 2-year warranty' }
];

const HomePage = () => {
  useSEO({
    title: 'Performance Tuning Modules for Petrol & Diesel Vehicles',
    description: 'BlueSpark Australia — plug-and-play tuning boxes that add up to 35% more power and torque. 2-year warranty, free Australia-wide shipping.',
    canonical: '/',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'BlueSpark Australia',
        url: 'https://bluesparkautomotive.com.au',
        logo: 'https://bluesparkautomotive.com.au/brand/bluespark-logo.png',
        description: 'Plug-and-play performance tuning modules for petrol and diesel vehicles. UK engineered, 2-year warranty.',
        areaServed: 'AU',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'BlueSpark Australia',
        url: 'https://bluesparkautomotive.com.au',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://bluesparkautomotive.com.au/products?search={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  });

  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsData, categoriesData] = await Promise.all([
          reviewsAPI.getAll({ limit: 6 }),
          categoriesAPI.getAll()
        ]);
        setReviews(reviewsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const iconMap = { Zap, Gauge, Wrench, Shield, Smartphone, Award };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Carousel */}
      <HeroCarousel />

      {/* Find My Vehicle */}
      <FindMyVehicle />
      
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              BlueSpark Australia
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-6">
              Petrol & Diesel Tuning Boxes
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Increase Power • Enhance Torque • Improve Fuel Economy
            </p>
            <p className="text-lg mb-10 text-blue-50 max-w-2xl mx-auto">
              Easily installed, traceless once removed. Professional-grade tuning solutions for petrol and diesel vehicles.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8">
                  Browse Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/products?category=pro-petrol">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8">
                  Petrol Tuning
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose BlueSpark?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Product Categories</h2>
          {loading ? (
            <p className="text-center">Loading categories...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <Link key={category.id} to={`/products?category=${category.id}`}>
                  <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-blue-600 hover:shadow-lg transition-all duration-300 h-full">
                    <h3 className="font-bold text-xl mb-2 text-blue-600">{category.name}</h3>
                    <p className="text-gray-600">{category.description}</p>
                    <div className="mt-4 flex items-center text-blue-600 font-medium">
                      View Products <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Recent Reviews</h2>
            <div className="flex items-center justify-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="ml-2 text-lg font-semibold">5.0 / 5.0</span>
            </div>
          </div>
          {loading ? (
            <p className="text-center">Loading reviews...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}

          <div className="max-w-3xl mx-auto mt-12">
            <ReviewForm
              onSubmitted={(r) => setReviews((prev) => [r, ...prev].slice(0, 6))}
            />
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Enhance Your Vehicle?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who have unlocked their vehicle's true potential with BlueSpark tuning solutions.
          </p>
          <Link to="/products">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8">
              Shop Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
